import { useInfinitePagedList } from "@/lib/useInfinitePagedList";
import type { Furniture } from "@/types/api";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { inventoryApi } from "./api";
import { furnitureKeys } from "./queryKeys";
import { SearchHelpDialog } from "./SearchHelpDialog";
import { useInventoryAdminUi } from "./uiStore";

const PAGE_SIZE = 50;

export function InventoryAdminPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const {
    rawSearch,
    search,
    helpOpen,
    previewImg,
    deleteTarget,
    setRawSearch,
    setSearch,
    setHelpOpen,
    setPreviewImg,
    setDeleteTarget,
  } = useInventoryAdminUi();

  useEffect(() => {
    const t = setTimeout(() => setSearch(rawSearch), 350);
    return () => clearTimeout(t);
  }, [rawSearch, setSearch]);

  const { items, total, isLoading, isFetchingNextPage, sentinelRef } = useInfinitePagedList<
    Furniture
  >({
    queryKey: furnitureKeys.adminList(search),
    fetchPage: ({ skip, limit }) =>
      inventoryApi.listFurniture({ skip, limit, search, sort: "size" }),
    pageSize: PAGE_SIZE,
  });

  const remove = useMutation({
    mutationFn: inventoryApi.removeFurniture,
    onSuccess: () => qc.invalidateQueries({ queryKey: furnitureKeys.lists() }),
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper>
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ flex: 1, minWidth: 140 }}>
            Inventory{" "}
            {!isLoading && (
              <Typography component="span" variant="body2" color="text.secondary">
                ({total})
              </Typography>
            )}
          </Typography>
          <TextField
            size="small"
            placeholder="Search"
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            sx={{ flex: { xs: 1, sm: "0 0 auto" }, minWidth: 140 }}
            InputProps={{
              endAdornment: rawSearch
                ? (
                  <IconButton size="small" onClick={() => setRawSearch("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
                : undefined,
            }}
          />
          <Tooltip title="Search help">
            <IconButton onClick={() => setHelpOpen(true)}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add item">
            <IconButton color="primary" onClick={() => void nav("/administration/inventory/new")}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: { xs: 720, sm: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell>NAME</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>CAT.</TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>SIZE</TableCell>
                <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>TAGS</TableCell>
                <TableCell align="right">AVAIL/QTY</TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {items.map((f) => {
                const total = f.items?.length ?? 0;
                const avail = (f.items ?? []).filter((i) =>
                  (i.status === 0 || i.status === 1) && i.room == null
                ).length;
                const w = f.width != null ? f.width : "-";
                const h = f.height != null ? f.height : "-";
                const d = f.length != null ? f.length : "-";
                const thumbSrc = f.imageFile?.thumbnailPath40;
                const tags = f.tags ?? [];
                return (
                  <TableRow key={f.id} hover>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={thumbSrc ?? undefined}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "grey.200",
                          cursor: thumbSrc ? "pointer" : "default",
                        }}
                        onClick={() =>
                          thumbSrc
                          && setPreviewImg({
                            src: f.imageFile?.thumbnailPath280 ?? f.imageFile?.relativePath
                              ?? thumbSrc,
                            name: f.name,
                          })}
                      />
                    </TableCell>
                    <TableCell>{f.name}</TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      {f.categoryInfo?.name ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{ whiteSpace: "nowrap", display: { xs: "none", md: "table-cell" } }}
                    >
                      {`${w}w ${h}h ${d}d`}
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {tags.slice(0, 4).map((t) => (
                          <Chip key={t.id} label={t.name} size="small" />
                        ))}
                        {tags.length > 4
                          && (
                            <Tooltip title={`${tags.length - 4} others`}>
                              <Chip label={`...`} size="small" />
                            </Tooltip>
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {avail}/{total}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            void nav(`/administration/inventory/${f.id}`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setDeleteTarget(f)}
                          disabled={remove.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No items found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div ref={sentinelRef} />

        {isFetchingNextPage && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Paper>

      <SearchHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete furniture?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            &ldquo;{deleteTarget?.name}&rdquo; will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={() => {
              if (deleteTarget) {
                remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!previewImg} onClose={() => setPreviewImg(null)} maxWidth="md">
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "white" }}>
          <IconButton
            size="small"
            onClick={() => setPreviewImg(null)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, bgcolor: "white" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {previewImg && (
            <Box sx={{ p: 2, pt: 5, textAlign: "center" }}>
              <Typography variant="subtitle1" gutterBottom>
                {previewImg.name}
              </Typography>
              <Box
                component="img"
                src={previewImg.src}
                alt={previewImg.name}
                sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
